import {CustomerAssistance} from "../components/customer-assistance";
import { CustomerLanguage } from "../components/customer-language";
import { NewOpportunityButton } from "../components/new-opportunity-dialog";
import {CustomerTimeline} from "../components/customer-timeline";
import type {Mail} from "../components/customer-correspondence";
import {CustomerRelationships} from "../components/customer-relationships";
import {CustomerActions} from "../components/customer-actions";
import {CustomerCorrespondence} from "../components/customer-correspondence";
import {ActivityHistory} from "../components/activity-history";
// Contact 360: the person, their leads, jobs, quotes, measured homes (S9),
// messages, photos and a timeline. The person's OWN details are editable here
// (`ContactEdit` → update_contact); everything else is read-only and changes
// through a proposal. Money is gold and staff-only.
import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { cn } from "@aiw/ui";
import { Disclaimer } from "@aiw/config";
import { Page, Panel } from "../shell/Page";
import { useContact, useContactQuotes, useContactLeads, useContactHomes, useContactJobs, useContactMessages, useQuotesFor, type ContactHome, type Lead } from "../hooks/slice-a";
import { aud, day, m2, sourceLabel, time } from "../lib/format";
import { Avatar, Chip, Kv, OutcomePill, Pill, Seg, StagePill, Tabs } from "../components/job-bits";
import { costWords, dispositionWords } from "../components/lead-drawer";
import { CustomerPhotos, useContactPhotos } from "../components/customer-photos";
import { ContactEdit } from "../components/contact-edit";
import { FollowUpForm } from "../components/follow-up-form";
import { lifecycleTone } from "./Contacts";
import { useAccount } from "../hooks/useReads";

type Tab = "overview" | "leads" | "jobs" | "quotes" | "homes" | "messages" | "photos" | "timeline" | "email" | "relationships";

export function Contact({contactId}: {contactId?:string} = {}) {
  const { id: routeId = "" } = useParams();
  const id = contactId ?? routeId;
  const { contact, isPending, isError } = useContact(id);
  const account = useAccount(contact?.account_id ?? "");
  const jobsQ = useContactJobs(id), {jobs} = jobsQ;
  const leadsQ = useContactLeads(id), leads = leadsQ.data ?? [];
  const quotesQ = useContactQuotes(id,jobs.map(j=>j.ref)), {quotes} = quotesQ;
  const homes = useContactHomes(id);
  const messages = useContactMessages(id);
  const photosQ = useContactPhotos(id);
  const [tab, setTab] = useState<Tab>("messages");
  const [selectedMail,setSelectedMail]=useState<Mail|null>(null);

  if (isPending) return <Page title="Contact" lede="…"><p className="text-sm text-muted-foreground">Loading…</p></Page>;
  if (isError) return <Page title="Contact unavailable"><p role="alert">The customer record could not be loaded. Refresh before acting.</p></Page>;
  if (!contact) return <Page title="No such contact"><p className="text-sm text-muted-foreground">Nobody at this id. <Link className="text-primary" to="/contacts">Back to contacts</Link></p></Page>;

  const firstLead = leads.slice().sort((a, b) => a.received_at.localeCompare(b.received_at))[0];
  const won = jobs.some((j) => j.outcome === "won" || j.outcome === "completed");
  const hs = homes.data ?? [];
  const ms = (messages.data ?? []).slice().sort((a, b) => b.at.localeCompare(a.at));
  const sections = [["Photos",photosQ], ["Quotes",quotesQ], ["Jobs",jobsQ], ["Leads",leadsQ], ["Homes",homes], ["Contact log",messages]] as const;
  const loading = sections.filter(([,q])=>q.isPending).map(([name])=>name);
  const failed = sections.filter(([,q])=>q.isError).map(([name])=>name);
  const incomplete = loading.length > 0 || failed.length > 0;


  const jobsTable = (
    <table className="w-full text-sm">
      <thead className="text-left font-mono text-micro uppercase text-muted-foreground"><tr><th className="pb-1 font-medium">Ref</th><th className="pb-1 font-medium">What</th><th className="pb-1 font-medium">Stage</th><th className="pb-1 text-right font-medium">Value on file</th><th className="pb-1" /></tr></thead>
      <tbody>
        {jobs.length === 0 && <tr><td colSpan={5} className="py-3 text-muted-foreground">{jobsQ.isPending ? "Loading jobs…" : jobsQ.isError ? "Jobs unavailable." : "No related jobs were returned."}</td></tr>}
        {jobs.map((j) => {
          const q = quotes.filter((x) => x.job_ref === j.ref);
          const primary = q.find((x) => x.is_primary) ?? q[0];
          return (
            <tr key={j.ref} className="border-t align-top">
              <td className="py-2 pr-2 font-mono text-micro"><Link className="text-primary" to={`/jobs/${j.ref}`}>{j.ref}</Link></td>
              <td className="py-2 pr-2">{j.product ?? <span className="text-muted-foreground">product to confirm</span>}{j.area_m2 ? ` · ${m2(j.area_m2)}` : ""}{j.stairs ? ` + ${j.stairs} stairs` : ""}</td>
              <td className="py-2 pr-2"><StagePill stage={j.effective_stage} /></td>
              <td className="num py-2 pr-2 text-right text-gold">{primary?.subtotal != null ? `${aud(primary.subtotal)} + GST` : j.deal_value != null ? `${aud(Math.round(j.deal_value / 1.1 * 100) / 100)} + GST` : <span className="text-muted-foreground">—</span>}</td>
              <td className="py-2 text-right"><Link className="text-micro text-primary" to={`/jobs/${j.ref}`}>Open</Link></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const quotesList = (
    <ul className="divide-y">
      {quotes.length === 0 && <li className="py-3 text-sm text-muted-foreground">{quotesQ.isPending ? "Loading quotes…" : quotesQ.isError ? "Quotes unavailable." : "No related quote records were returned."}</li>}
      {quotes.map((q) => (
        <li key={q.id} className="flex items-center gap-2 py-2 text-sm">
          <span className="min-w-0 flex-1"><span className="font-medium">{q.option_name ?? "Option"}</span>{q.is_primary && <Pill tone="info" className="ml-1.5">primary</Pill>}<span className="block text-micro text-muted-foreground"><span className="font-mono">{q.job_ref}</span> · {day(q.created_at)}</span></span>
          <span className="num text-gold">{q.subtotal == null ? '—' : `${aud(q.subtotal)} + GST`}</span>
          <Link className="text-micro text-primary underline-offset-2 hover:underline" to={`/quotes/${q.id}`}>Open quote</Link>
        </li>
      ))}
    </ul>
  );

  const leadsTable = (
    <table className="w-full text-sm">
      <thead className="text-left font-mono text-micro uppercase text-muted-foreground"><tr><th className="pb-1 font-medium">Received</th><th className="pb-1 font-medium">Source · cost</th><th className="pb-1 font-medium">Brief</th><th className="pb-1 font-medium">Disposition</th><th className="pb-1 font-medium">Outcome</th></tr></thead>
      <tbody>
        {leads.length === 0 && <tr><td colSpan={5} className="py-3 text-muted-foreground">{leadsQ.isPending ? "Loading leads…" : leadsQ.isError ? "Leads unavailable." : "No related leads were returned."}</td></tr>}
        {leads.map((l) => (
          <tr key={l.id} className="border-t align-top">
            <td className="num py-2 pr-2 text-micro">{day(l.received_at)}</td>
            <td className="py-2 pr-2"><Chip>{sourceLabel(l.source_id || "none")}{l.facet ? ` · ${l.facet}` : ""}</Chip> <span className="num text-micro text-gold">{costWords(l)}</span></td>
            <td className="py-2 pr-2 text-muted-foreground">{l.raw_brief ? `“${l.raw_brief}”` : "—"}</td>
            <td className="py-2 pr-2">{dispositionWords(l.disposition)}{l.disposition_reason ? <span className="text-micro text-muted-foreground"> · {l.disposition_reason}</span> : null}</td>
            <td className="py-2"><OutcomePill outcome={l.outcome} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const photosGrid = <CustomerPhotos key={id} contactId={id} photos={photosQ.data ?? []} loading={photosQ.isPending} error={photosQ.error}/>;

  const contactCard = (
    <Panel title="Contact" meta={<ContactEdit contact={contact} />}>
      <Kv rows={[
        ["Name", contact.name ?? "—"],
        ["Customer address", contact.address ?? "Not recorded"],
        ["Email", contact.email ?? <span className="text-muted-foreground">none</span>],
        ["Phone", <>{contact.phone ?? <span className="text-muted-foreground">none</span>}{contact.prefers ? <span className="text-muted-foreground"> · prefers {contact.prefers}</span> : null}</>],
        ["Lifecycle", <Pill tone={lifecycleTone(contact.lifecycle)}>{contact.lifecycle ?? "unknown"}{won ? " · won" : ""}</Pill>],
        ["Related jobs", jobsQ.isPending ? "Loading…" : jobsQ.isError ? "Unavailable" : String(jobs.length)],
        ["Prefers", contact.prefers ?? <span className="text-muted-foreground">not recorded</span>],
        ["Since", day(contact.created_at)],
      ]} />
    </Panel>
  );

  const accountCard = (
    <Panel title="Account" meta="read-only">
      <Kv rows={[
        ["Account", account.data ? <Link className="text-primary hover:underline" to={`/accounts/${account.data.id}`}>{String(account.data.name ?? "Unnamed")}</Link> : <span className="text-muted-foreground">no business — bills the person</span>],
        ["Address", (hs[0] ? homeAddress(hs[0]) : null) ?? jobs[0]?.address ?? <span className="text-muted-foreground">none on file</span>],
        ["Homes", homes.isPending ? "Loading…" : homes.isError ? "Unavailable" : hs.length ? `${hs.length} registered` : "none registered"],
        ["Tags", <span className="flex flex-wrap gap-1">{[...new Set(jobs.flatMap((j) => j.tags ?? []))].map((t) => <Chip key={t}>{t}</Chip>)}{firstLead && <Chip>{sourceLabel(firstLead.source_id || "none")}</Chip>}</span>],
      ]} />
    </Panel>
  );

  const timeline = <div className="space-y-4"><ContactTimeline leads={leads} jobs={jobs} quotes={quotes} homes={hs} messages={ms} incomplete={incomplete} /><ActivityHistory contactId={id} /></div>;

  return (
    <div className={contactId ? "min-w-0" : "page py-5"}>
      <div className="mb-3 flex items-center gap-2 text-micro text-muted-foreground"><Link className="text-primary" to="/contacts">Contacts</Link><span>/</span><span>Contacts</span></div>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-4">
          <Avatar name={contact.name} size="lg" />
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">{contact.name ?? "Unnamed"}</h1>
            <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
              {contact.suburb && <span>{contact.suburb}</span>}{contact.phone && <span>· {contact.phone}</span>}{contact.email && <span>· {contact.email}</span>}
              {firstLead && <span>· <Chip>{sourceLabel(firstLead.source_id || "none")}</Chip>{firstLead.facet && firstLead.source_id === "referral" ? ` from ${firstLead.facet}` : ""}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NewOpportunityButton contactId={contact.id} name={contact.name ?? "this customer"} />
          <Pill tone={lifecycleTone(contact.lifecycle)}>{contact.lifecycle ?? "unknown"}{won ? " · won" : ""}</Pill>
          {/* There was a "Platform ↗" button here pointing at the old platform's
              copy of this very person. The CRM owns /contacts/:id, so it was a
              link out to a duplicate of the page you were already on. */}
        </div>
      </header>

      <CustomerActions contact={contact}/><CustomerLanguage contactId={contact.id}/><CustomerAssistance contactId={id}/>
      <FollowUpForm key={contact.id} subject={{contact_id:contact.id}} name={contact.name ?? "this customer"}/>
      {loading.length > 0 && <p role="status" className="mb-3 text-sm text-muted-foreground">Loading {loading.join(", ")}… Counts and history are not complete yet.</p>}
      {failed.length > 0 && <p role="alert" className="mb-3 text-sm text-destructive">Could not load {failed.join(", ")}. Counts and history are incomplete; refresh before acting.</p>}
      <Tabs value={tab} onChange={setTab} items={[
        { id: "overview", label: "Overview" }, {id:"relationships",label:"Relationships"}, { id: "leads", label: "Leads", n: leadsQ.isPending || leadsQ.isError ? undefined : leads.length }, { id: "jobs", label: "Jobs", n: jobsQ.isPending || jobsQ.isError ? undefined : jobs.length }, { id: "quotes", label: "Quotes", n: quotesQ.isPending || quotesQ.isError ? undefined : quotes.length },
        { id: "homes", label: "Homes", n: homes.isPending || homes.isError ? undefined : hs.length }, { id: "messages", label: "Contact log", n: messages.isPending || messages.isError ? undefined : ms.length }, { id: "photos", label: "Photos", n: photosQ.isPending || photosQ.isError ? undefined : photosQ.data?.length }, { id: "timeline", label: "Timeline" }, {id:"email",label:"Email"},
      ]} />

      {tab === "overview" && (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div className="space-y-4">
            {hs[0] ? <HomeCard home={hs[0]} /> : <Panel title="Home"><p className="text-sm text-muted-foreground">{homes.isPending ? "Loading homes…" : homes.isError ? "Homes unavailable." : "No registered home was returned."}</p></Panel>}
            <Panel title="Jobs & quotes">{jobsTable}</Panel>
            <Panel title="Photos" meta="saved files">{photosGrid}</Panel>
          </div>
          <div className="space-y-4">{contactCard}{accountCard}{timeline}</div>
        </div>
      )}
      {tab === "leads" && <Panel title="Leads">{leadsTable}</Panel>}
      {tab === "jobs" && <Panel title="Jobs">{jobsTable}</Panel>}
      {tab === "quotes" && <Panel title="Quotes" meta={<span className="rounded-full bg-gold/10 px-2 text-gold">staff only</span>}>{quotesList}</Panel>}
      {tab === "homes" && (hs.length ? <div className="space-y-4">{hs.map((h) => <HomeCard key={h.id} home={h} />)}</div> : <Panel title="Homes"><p className="text-sm text-muted-foreground">{homes.isPending ? "Loading homes…" : homes.isError ? "Homes unavailable." : "No registered home was returned."}</p></Panel>)}
      {tab === "messages" && <Panel title="Customer communication" meta="email and recorded activity">{<CustomerTimeline contactId={id} onEmail={m=>{setSelectedMail(m??null);setTab("email")}}/>}</Panel>}
      {tab === "relationships" && <CustomerRelationships contact={contact}/>}
      {tab === "email" && <CustomerCorrespondence key={id} contactId={id} initialMail={selectedMail}/> }
      {tab === "photos" && <Panel title="Photos">{photosGrid}</Panel>}
      {tab === "timeline" && timeline}
    </div>
  );
}

function homeAddress(home: ContactHome): string {
  const a = home.address ?? {};
  return [a.unit,a.street_number,a.street_name,a.suburb ?? home.suburb,a.state,a.postcode ?? home.postcode].filter(Boolean).join(" ") || home.label || "Address not recorded";
}
function HomeCard({home}: {home:ContactHome}) {
  return <Panel title={`Home · ${homeAddress(home)}`}>
    {home.measurements.length === 0 && <p className="text-sm text-muted-foreground">No saved measurement is attached to this home.</p>}
    {home.measurements.slice().sort((a,b)=>b.measured_at.localeCompare(a.measured_at)).map(measure=><div key={measure.id} className="mb-4">
      <p className="text-sm">Measured {day(measure.measured_at)} · {measure.method}{measure.job_ref && <> · <Link className="text-primary" to={`/jobs/${measure.job_ref}`}>{measure.job_ref}</Link></>}</p>
      {(measure.disclaimer_required || measure.method !== "laser") && <p className="text-sm text-muted-foreground">{Disclaimer.defaults.no_laser_measure}</p>}
      <ul className="mt-2 text-sm">{(measure.summary.rooms ?? []).map((r,i)=><li key={i} className="flex justify-between gap-3 border-b py-1"><span>{r.floor} · {r.name}{r.finish ? ` · ${r.finish}` : ""}<span className="block text-micro text-muted-foreground">{r.source === "tape" ? "Measured dimensions" : r.source === "sketch" ? "Scaled sketch" : "Scale not recorded"}</span></span><span className="num">{r.laid_m2 == null ? "Area unavailable" : m2(r.laid_m2)}</span></li>)}</ul>
      {measure.summary.area_m2 != null && <p className="mt-2 text-sm">Recorded total · {m2(measure.summary.area_m2)}</p>}
    </div>)}
  </Panel>;
}

type Ev = { at: string; kind: "key" | "money" | "plain"; title: string; detail?: ReactNode };

function ContactTimeline({ leads, jobs, quotes, homes, messages, incomplete }: { leads: Lead[]; jobs: ReturnType<typeof useContactJobs>["jobs"]; quotes: ReturnType<typeof useQuotesFor>["quotes"]; incomplete: boolean; homes: ContactHome[]; messages: { at: string; direction: string; channel: string; who: string; text: string }[] }) {
  const [f, setF] = useState<"key" | "all">("key");
  const evs: Ev[] = [];
  for (const l of leads) {
    evs.push({ at: l.received_at, kind: "plain", title: "Lead", detail: <>{sourceLabel(l.source_id || "none")}{l.facet ? ` · ${l.facet}` : ""} · <span className="num text-gold">{costWords(l)}</span></> });
    if (l.disposition_at) evs.push({ at: l.disposition_at, kind: "plain", title: dispositionWords(l.disposition), detail: l.disposition_reason ?? undefined });
  }
  for (const h of homes) for (const m of h.measurements) evs.push({at:m.measured_at,kind:"key",title:"Measurement recorded",detail:`${homeAddress(h)} · ${m.method}`});
  for (const j of jobs) {
    if (j.creation_date) evs.push({ at: j.creation_date, kind: "key", title: "Job created", detail: <span className="font-mono text-micro">{j.ref}</span> });
    if (j.deposit_date) evs.push({ at: j.deposit_date, kind: "money", title: "Deposit date recorded" });
    if (j.install_next_date) evs.push({ at: j.install_next_date, kind: "key", title: "Installation scheduled", detail: j.product ?? undefined });
    if (j.close_date) evs.push({ at: j.close_date, kind: "key", title: "Job closed", detail: j.outcome ?? "Outcome not recorded" });
    if (j.date_paid) evs.push({ at: j.date_paid, kind: "money", title: "Paid date recorded" });
  }
  for (const q of quotes) evs.push({ at: q.created_at, kind: "money", title: "Quote record created", detail: <>{q.option_name} · <span className="num text-gold">{q.subtotal == null ? '—' : `${aud(q.subtotal)} + GST`}</span></> });
  for (const m of messages) evs.push({ at: m.at, kind: "plain", title: `${m.channel === "sms" ? "SMS" : m.channel.charAt(0).toUpperCase() + m.channel.slice(1)} ${m.direction === "in" ? "in" : "out"}`, detail: `${m.who}: “${m.text}”` });
  const shown = evs.filter((e) => f === "all" || e.kind !== "plain").sort((a, b) => b.at.localeCompare(a.at));
  return (
    <Panel title="Timeline" meta={<Seg value={f} onChange={setF} items={[{ id: "key", label: "Key" }, { id: "all", label: "All" }]} />}>
      <p className="mb-3 text-micro text-muted-foreground">Recorded dates and contact notes. Scheduled dates do not confirm attendance or completion.</p>
      {incomplete && <p className="mb-3 text-sm text-muted-foreground">History is incomplete while related records are unavailable.</p>}
      {shown.length === 0 && <p className="text-sm text-muted-foreground">Nothing yet.</p>}
      <ul className="space-y-2">
        {shown.map((e, i) => (
          <li key={`${e.at}-${i}`} className="grid grid-cols-[4.5rem_auto_minmax(0,1fr)] items-start gap-2 text-sm">
            <span className="num pt-0.5 text-micro text-muted-foreground">{day(e.at)}</span>
            <span className={cn("mt-1.5 h-2 w-2 rounded-full", e.kind === "key" ? "bg-primary" : e.kind === "money" ? "bg-gold" : "bg-border")} aria-hidden />
            <span className="min-w-0"><span className="font-medium">{e.title}</span>{e.detail ? <span className="text-muted-foreground"> · {e.detail}</span> : null}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
